import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageHero } from '@/components/shared/PageHero';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { OFFICIALS } from '@/data/mockData';
import { Crown, Search, Users, UserCircle2 } from 'lucide-react';

const groupOf = (position) => {
  if (/Punong/i.test(position)) return 'Executive';
  if (/Kagawad/i.test(position)) return 'Sangguniang Barangay';
  if (/SK/i.test(position)) return 'Sangguniang Kabataan';
  if (/Secretary|Treasurer/i.test(position)) return 'Administrative Officers';
  if (/Lupon/i.test(position)) return 'Lupong Tagapamayapa';
  return 'Others';
};

const Officials = () => {
  const [q, setQ] = useState('');
  const grouped = useMemo(() => {
    const filtered = OFFICIALS.filter((o) =>
      [o.name, o.position, o.committee].join(' ').toLowerCase().includes(q.toLowerCase())
    );
    return filtered.reduce((acc, o) => {
      const g = groupOf(o.position);
      acc[g] = acc[g] || [];
      acc[g].push(o);
      return acc;
    }, {});
  }, [q]);

  const order = ['Executive', 'Sangguniang Barangay', 'Sangguniang Kabataan', 'Administrative Officers', 'Lupong Tagapamayapa', 'Others'];

  return (
    <div data-testid="officials-page">
      <PageHero
        eyebrow="Our Leaders"
        title="Barangay Officials"
        subtitle="Meet the elected officials, appointed officers, and committee chairs serving Brgy San Isidro."
        breadcrumbs={[{ label: 'Officials' }]}
      >
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary-foreground/70" />
          <Input
            placeholder="Search by name, position, or committee..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white/10 border-white/30 placeholder:text-primary-foreground/70 text-primary-foreground focus-visible:ring-white/40"
            data-testid="officials-search"
          />
        </div>
      </PageHero>

      <section className="container-app section-padding">
        {order.map((g) => {
          const list = grouped[g];
          if (!list || list.length === 0) return null;
          return (
            <div key={g} className="mb-12 last:mb-0">
              <SectionHeader eyebrow={g} title={g === 'Executive' ? 'Punong Barangay' : g} className="mb-6" />
              <div className={g === 'Executive' ? 'grid' : 'grid sm:grid-cols-2 lg:grid-cols-3 gap-5'}>
                {g === 'Executive' ? (
                  list.map((o) => <FeatureOfficial key={o.id} o={o} />)
                ) : (
                  list.map((o) => <OfficialCard key={o.id} o={o} />)
                )}
              </div>
            </div>
          );
        })}

        {Object.keys(grouped).length === 0 && (
          <Card className="p-12 text-center border-dashed">
            <Users className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">No officials match your search.</p>
          </Card>
        )}
      </section>
    </div>
  );
};

const FeatureOfficial = ({ o }) => (
  <Card className="overflow-hidden border-0 shadow-elegant bg-gradient-to-br from-primary/5 via-card to-accent-soft" data-testid={`official-${o.id}`}>
    <div className="grid md:grid-cols-3 gap-0">
      <div className="aspect-square md:aspect-auto bg-secondary flex items-center justify-center">
        <div className="inline-flex h-24 w-24 items-center justify-center rounded-full bg-primary/10 text-primary">
          <UserCircle2 className="h-14 w-14" />
        </div>
      </div>
      <div className="md:col-span-2 p-8 lg:p-10 flex flex-col justify-center">
        <Badge className="self-start bg-primary text-primary-foreground hover:bg-primary"><Crown className="h-3 w-3 mr-1" /> {o.position}</Badge>
        <h3 className="mt-3 font-display text-3xl font-bold text-primary-deep">{o.name}</h3>
        <p className="mt-2 text-sm text-muted-foreground">Committee · {o.committee}</p>
        <p className="mt-4 text-sm text-foreground/80 leading-relaxed">
          Leading {o.committee.toLowerCase()} initiatives and the daily operations of the barangay.
        </p>
      </div>
    </div>
  </Card>
);

const OfficialCard = ({ o }) => (
  <Card className="overflow-hidden card-elevated border-0 group" data-testid={`official-${o.id}`}>
    <div className="aspect-[4/5] bg-secondary flex items-center justify-center">
      <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary">
        <UserCircle2 className="h-12 w-12" />
      </div>
    </div>
    <div className="p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">{o.position}</div>
      <h4 className="mt-1.5 font-heading font-semibold text-lg leading-snug">{o.name}</h4>
      <p className="mt-1 text-xs text-muted-foreground">{o.committee}</p>
    </div>
  </Card>
);

export default Officials;
